import {
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DialogProps } from '@material-ui/core';

import { Context, DialogContext } from '../DialogComposite/context';

export interface RootHandlers {
  open: () => void;
  close: () => void;
}

export type BaseDialogProps = Omit<
  DialogProps,
  'open' | 'onClose' | 'classes' | 'className'
> & {
  /* Width of the dialog, as passing maxWidth and fullWidth to DialogProps */
  width?: DialogProps['maxWidth'];
  children: ReactNode;
};

export type ControlledDialogProps = BaseDialogProps & {
  open?: boolean;
  onClose?: () => void;
  static?: undefined;
};

export type StaticDialogProps = BaseDialogProps & {
  static: true;
  onClose?: () => void;
  open?: never;
};

export type RootProps = ControlledDialogProps | StaticDialogProps;

// NOTE: useId from react not supported by Backstage react (v17)
let idCounter = 0;
const useId = () => {
  const idRef = useRef<string>();
  if (!idRef.current) {
    idRef.current = `id-${idCounter++}`;
  }
  return idRef.current;
};

const Root = forwardRef<RootHandlers, RootProps>((props, ref) => {
  const [openState, setOpenState] = useState(props.open ?? false);
  const id = useId();

  const {
    open = false,
    onClose,
    children,
    static: staticProp = false,
    width,
    ...dialogProps
  } = props;

  const handleOpen = useCallback(() => setOpenState(true), []);
  const handleClose = useCallback(() => {
    setOpenState(false);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    setOpenState(open);
  }, [open]);

  useImperativeHandle(
    ref,
    () => ({
      open: () => setOpenState(true),
      close: () => setOpenState(false),
    }),
    [],
  );

  const value: Context = useMemo(
    () => ({
      id,
      open: openState,
      static: staticProp,
      onClose: handleClose,
      onOpen: handleOpen,
      props: {
        ...dialogProps,
        // setting "width" is like setting "maxWidth" and "fullWidth"
        maxWidth: dialogProps.maxWidth ?? width,
        fullWidth: dialogProps.fullWidth ?? (width ? true : undefined),
      },
      isLoading: false,
    }),
    [openState, staticProp, handleClose, handleOpen, dialogProps],
  );

  return (
    <DialogContext.Provider value={value}>{children}</DialogContext.Provider>
  );
});

Root.displayName = 'DialogComposite.Root';

export default Root;
